import { useEffect, useState } from 'react'
import { RefreshCw, AlertTriangle, Building2, User, Copy, Loader2, FileSpreadsheet } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { EmptyState } from '@/components/ui/EmptyState'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { toast } from '@/components/ui/Toast'
import { brl, dateBR, dateTimeBR } from '@/lib/format'
import { exportToXlsx, type ExportColumn } from '@/lib/exportXlsx'
import { useAuthority } from '@/hooks/useAuthority'
import {
  DuplicateInvoicesService,
  type DuplicateGroup,
  type DuplicateInvoice,
} from '@/services/modules/DuplicateInvoicesService'

const MONTHS = ['', 'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number)
  return y && m ? `${MONTHS[m]} de ${y}` : ym
}
function statusVariant(s: string): 'green' | 'amber' | 'red' | 'violet' | 'gray' {
  return ({ PAGO: 'green', PENDENTE: 'amber', ISENTO: 'violet', CANCELADO: 'red', RECUSADO: 'red', PROCESSADO: 'gray' } as const)[s] ?? 'gray'
}
/** Fatura "mais recente" do grupo (maior createdAt). */
function newest(invoices: DuplicateInvoice[]): DuplicateInvoice {
  return invoices.reduce((a, b) => ((a.createdAt ?? '') >= (b.createdAt ?? '') ? a : b))
}

/** Linha "achatada" para exportação: uma por fatura duplicada, com os dados do grupo. */
type DuplicateExportRow = { group: DuplicateGroup; inv: DuplicateInvoice; isRecent: boolean }

const EXPORT_COLUMNS: ExportColumn<DuplicateExportRow>[] = [
  { header: 'Tipo',              accessor: (r) => (r.group.entityType === 'company' ? 'Empresa' : 'Associado') },
  { header: 'Associado/Empresa', accessor: (r) => r.group.entityName },
  { header: 'Mês',               accessor: (r) => monthLabel(r.group.month) },
  { header: 'Faturas no grupo',  accessor: (r) => r.group.count },
  { header: 'Código',            accessor: (r) => r.inv.code ?? '' },
  { header: 'Vencimento',        accessor: (r) => (r.inv.dueDate ? dateBR(r.inv.dueDate) : '') },
  { header: 'Valor',             accessor: (r) => brl(r.inv.amount) },
  { header: 'Status',            accessor: (r) => r.inv.status },
  { header: 'Criada em',         accessor: (r) => (r.inv.createdAt ? dateTimeBR(r.inv.createdAt) : '') },
  { header: 'Mais recente',      accessor: (r) => (r.isRecent ? 'Sim' : 'Não') },
]

export function IntegrationsDuplicateInvoicesPage() {
  const [groups, setGroups] = useState<DuplicateGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [target, setTarget] = useState<{ inv: DuplicateInvoice; group: DuplicateGroup } | null>(null)
  const [canceling, setCanceling] = useState(false)
  const [archiveTarget, setArchiveTarget] = useState<DuplicateGroup | null>(null)
  const [archiving, setArchiving] = useState(false)
  const { can } = useAuthority()
  const canCancel = can('financial.duplicate_invoices.cancel')
  const canArchive = can('financial.duplicate_invoices.archive')

  const load = () => {
    setLoading(true)
    DuplicateInvoicesService.list()
      .then(setGroups)
      .catch(() => toast.error('Não foi possível carregar as faturas duplicadas.'))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  async function confirmCancel() {
    if (!target) return
    setCanceling(true)
    try {
      const r = await DuplicateInvoicesService.cancel(target.inv.id)
      toast.success({ title: 'Fatura cancelada', description: r.message })
      setTarget(null)
      load()
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(msg ?? 'Falha ao cancelar a fatura.')
    } finally {
      setCanceling(false)
    }
  }

  async function confirmArchive() {
    if (!archiveTarget) return
    setArchiving(true)
    try {
      const r = await DuplicateInvoicesService.archive({ entity_type: archiveTarget.entityType, entity_id: archiveTarget.entityId, month: archiveTarget.month })
      toast.success({ title: 'Duplicidade arquivada', description: r.message })
      setArchiveTarget(null)
      load()
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(msg ?? 'Falha ao arquivar.')
    } finally {
      setArchiving(false)
    }
  }

  const totalDup = groups.reduce((sum, g) => sum + (g.count - 1), 0)

  function exportExcel() {
    const rows: DuplicateExportRow[] = groups.flatMap((group) => {
      const recent = newest(group.invoices)
      return group.invoices.map((inv) => ({ group, inv, isRecent: inv.id === recent.id }))
    })
    if (!rows.length) { toast.error('Nenhum registro para exportar.'); return }
    exportToXlsx('faturas-duplicadas', rows, EXPORT_COLUMNS)
  }

  return (
    <>
      <PageHeader
        title="Faturas duplicadas"
        subtitle="Faturas repetidas para o mesmo associado/empresa no mesmo mês"
        crumbs={[{ label: 'Financeiro' }, { label: 'Faturas duplicadas' }]}
        actions={
          <>
            <Button variant="secondary" onClick={exportExcel} disabled={loading || groups.length === 0} iconLeft={<FileSpreadsheet size={14} />}>
              Exportar Excel
            </Button>
            <Button variant="secondary" onClick={load} loading={loading} iconLeft={<RefreshCw size={14} />}>
              Recarregar
            </Button>
          </>
        }
      />

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-gray-500"><Loader2 size={16} className="animate-spin" /> Carregando…</div>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<Copy size={22} />}
          title="Nenhuma fatura duplicada"
          description="Não há faturas repetidas para o mesmo associado/empresa no mesmo mês. A geração de faturas agora é atômica — não duplica."
        />
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>
              <strong>{groups.length}</strong> grupo(s) com duplicidade · <strong>{totalDup}</strong> fatura(s) a mais.
              Cancele a duplicada (em geral a <strong>mais recente</strong>). Cancelar remove também as comissões geradas por ela.
            </span>
          </div>

          {groups.map((g) => {
            const recent = newest(g.invoices)
            return (
              <div key={`${g.entityId}-${g.month}`} className="rounded-md border border-gray-200 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
                  <div className="flex items-center gap-2">
                    {g.entityType === 'company'
                      ? <Building2 size={16} className="text-gray-400" />
                      : <User size={16} className="text-gray-400" />}
                    <span className="text-sm font-medium text-gray-900">{g.entityName}</span>
                    <Badge variant={g.entityType === 'company' ? 'blue' : 'violet'}>
                      {g.entityType === 'company' ? 'Empresa' : 'Associado'}
                    </Badge>
                    <span className="text-gray-300">·</span>
                    <span className="text-sm text-gray-600">{monthLabel(g.month)}</span>
                    <Badge variant="amber">{g.count} faturas</Badge>
                  </div>
                </div>

                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400">
                      <th className="px-4 py-2 font-medium">Código</th>
                      <th className="px-4 py-2 font-medium">Vencimento</th>
                      <th className="px-4 py-2 font-medium">Valor</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 font-medium">Criada em</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {g.invoices.map((inv) => {
                      const isRecent = inv.id === recent.id
                      return (
                        <tr key={inv.id} className={isRecent ? 'bg-amber-50/50' : ''}>
                          <td className="px-4 py-2 text-gray-700">{inv.code ?? '—'}</td>
                          <td className="px-4 py-2 text-gray-700">{inv.dueDate ? dateBR(inv.dueDate) : '—'}</td>
                          <td className="px-4 py-2 font-medium text-gray-900">{brl(inv.amount)}</td>
                          <td className="px-4 py-2"><Badge variant={statusVariant(inv.status)}>{inv.status}</Badge></td>
                          <td className="px-4 py-2 text-gray-500">
                            {inv.createdAt ? dateTimeBR(inv.createdAt) : '—'}
                            {isRecent && <span className="ml-2 text-[11px] font-medium text-amber-600">mais recente</span>}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {canCancel && (
                              <button
                                type="button"
                                disabled={inv.paid}
                                onClick={() => setTarget({ inv, group: g })}
                                title={inv.paid ? 'Fatura paga — estorne pelo gateway' : 'Cancelar esta fatura'}
                                className="cursor-pointer rounded-md px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                Cancelar
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

                {(canArchive || canCancel) && (
                <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-2.5">
                  {canArchive && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setArchiveTarget(g)}
                    title="Duplicidade legítima — arquivar para sair desta lista (as faturas continuam válidas)"
                  >
                    Arquivar (duplicidade legítima)
                  </Button>
                  )}
                  {canCancel && (
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={recent.paid}
                    onClick={() => setTarget({ inv: recent, group: g })}
                    title={recent.paid ? 'A mais recente está paga — estorne pelo gateway' : undefined}
                  >
                    Cancelar a mais recente
                  </Button>
                  )}
                </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!target}
        title="Cancelar fatura duplicada"
        description={
          target
            ? `Cancelar a fatura ${target.inv.code ?? ''} (${brl(target.inv.amount)}) de ${target.group.entityName}? As comissões geradas por ela também serão removidas. Esta ação não pode ser desfeita.`
            : ''
        }
        confirmLabel="Cancelar fatura"
        variant="danger"
        loading={canceling}
        onConfirm={confirmCancel}
        onClose={() => setTarget(null)}
      />

      <ConfirmDialog
        open={!!archiveTarget}
        title="Arquivar duplicidade"
        description={
          archiveTarget
            ? `Arquivar a duplicidade de ${archiveTarget.entityName} em ${monthLabel(archiveTarget.month)}? As ${archiveTarget.count} faturas continuam válidas — apenas deixam de aparecer aqui.`
            : ''
        }
        confirmLabel="Arquivar"
        loading={archiving}
        onConfirm={confirmArchive}
        onClose={() => setArchiveTarget(null)}
      />
    </>
  )
}

export default IntegrationsDuplicateInvoicesPage
